import React, { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ManageHistoryOutlinedIcon from "@mui/icons-material/ManageHistoryOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "../../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../../assets/css/PrimeGrid.css";

import { saveAs } from "file-saver";

import { AlertContext } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { ConfiguracionContext } from "../../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../../theme/brand";
import Spinner from "../../Spinner";
import Barra from "../../Barra";

const exportColumns = [
  { title: "ID", dataKey: "id" },
  { title: "SKU", dataKey: "sku" },
  { title: "Nombre", dataKey: "nombre" },
  { title: "Almacen", dataKey: "almacen" },
  { title: "Stock Anterior", dataKey: "stockAnterior" },
  { title: "Stock Posterior", dataKey: "stockPosterior" },
  { title: "Usuario", dataKey: "usuario" },
];

export default function AjustesStocksMedicamentos() {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const {
    statusAcceso,
    configuracion,
    ajustesStocksMedicamentos,
    obtenerAjustesStocksMedicamentos,
    obtenerAcceso,
  } = useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({
        usuarioId: state.usuario.id,
        layout: "AjustesStocksMedicamentos",
      });
    }
    obtenerAjustesStocksMedicamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error"
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const exitClick = () => navigate("/");

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default(0, 0);
        doc.setFontSize(12);
        doc.text("Listado de Ajustes de Medicamentos", 60, 8);
        doc.autoTable(exportColumns, ajustesStocksMedicamentos || []);
        window.open(doc.output("bloburl"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(ajustesStocksMedicamentos || []);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `ajustesstocksdemedicamentos_export_${new Date().getTime()}.xlsx`);
    });
  };

  const cargando = statusAcceso === 0 || statusAcceso === 400;

  return (
    <Fragment>
      <Barra />

      {cargando ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {ajustesStocksMedicamentos ? (
              <Fragment>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <ManageHistoryOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          Ajustes de Stock
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${ajustesStocksMedicamentos.length} registros`}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ flexGrow: 1 }} />

                    <TextField
                      size="small"
                      placeholder="Buscar…"
                      onInput={(e) => setGlobalFilter(e.target.value)}
                      sx={{ minWidth: { xs: "100%", md: 240 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={exportExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="text"
                      color="inherit"
                      startIcon={<CloseIcon />}
                      onClick={exitClick}
                    >
                      Cerrar
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <DataTable
                    value={ajustesStocksMedicamentos}
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    selectionMode="single"
                    dataKey="id"
                    className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                    globalFilter={globalFilter}
                    emptyMessage="No hay datos."
                    paginator
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50]}
                  >
                    <Column field="id" header="ID" sortable style={{ width: "7rem" }} />
                    <Column field="sku" header="SKU" sortable style={{ width: "9rem" }} />
                    <Column field="nombre" header="Nombre" sortable />
                    <Column field="almacen" header="Almacén" sortable />
                    <Column field="stockAnterior" header="Stock Anterior" sortable style={{ width: "9rem" }} />
                    <Column field="stockPosterior" header="Stock Posterior" sortable style={{ width: "9rem" }} />
                    <Column field="usuario" header="Usuario" sortable />
                  </DataTable>
                </Paper>
              </Fragment>
            ) : (
              <Spinner />
            )}
          </Box>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
